import React, { useMemo } from 'react'
import { useNewTranslation } from 'hooks/index'
import { GridColDef, GridRenderCellParams, GridValueGetterParams, useGridApiRef } from '@mui/x-data-grid-pro'
import { theme } from 'theme'
import { Box, Flex, Text } from 'rebass'
import styled from 'particles/themes/styled'
import { SimpleStringCell } from 'components/Shared/MuiTable/Cell/SimpleStringCell'
import _ from 'lodash'
import ButtonPrimary from 'components/Shared/Button/OldButton/ButtonPrimary'
import { ActionType } from '../Submissions/Review/Registrations/ListApprovalModal'
import { styled as muiStyled } from '@mui/material/styles'
import { StyledDataGrid } from './MuiTable'
import { ERROR_CODES } from 'state/error'

const ScrollBox = styled(Box)`
    width: 100%;
    max-height: 70vh;
    overflow-y: auto;
    box-sizing: content-box;
`

const MassImportResultDataGrid = muiStyled(StyledDataGrid)`
    margin: ${theme.spacing.xxs} 0 ${theme.spacing.xs};
    .MuiDataGrid-cell {
        position: relative;
    }
    .textCenter {
        justify-content: center;
    }
`

const TableContainer = styled.div`
    ol {
        counter-reset: list;
    }
    ol > li {
        list-style: none;
    }
    ol > li:before {
        counter-increment: list;
        content: '(' counter(list) ') ';
    }
`

const DescriptionText = styled(Text)`
    color: ${theme.colors.mineShaft};
    margin-bottom: ${theme.spacing.xs};
    margin-left: ${theme.spacing.xs};
    /* Rebass Text emits line-height: 1.5 via emotion by default. Because emotion injects after
       styled-components in <head>, Rebass wins same-specificity rules. && doubles specificity to
       ensure our explicit line-height overrides the Rebass default. */
    && {
        line-height: 28px;
    }
`

interface CountBoxProps {
    title: string
    count: number
    color?: string
}

const CountBox: React.FC<CountBoxProps> = ({ title, count, color }) => {
    return (
        <Box textAlign="center">
            <Text fontSize="18px">{title}</Text>
            <Text fontSize="36px" color={color}>
                {count}
            </Text>
        </Box>
    )
}

interface TableProps {
    rows: RowState[]
    title: string
    color: string
    isWaitlistAction?: boolean
    isPayout?: boolean
}

const Table: React.FC<TableProps> = ({ rows, title, color, isWaitlistAction, isPayout = false, ...props }) => {
    const [t] = useNewTranslation()
    const tableRef = useGridApiRef()

    const [usernames, textColor] = useMemo(() => {
        if (rows.length === 0) {
            return [[{ id: 0, username: '', message: 'N/A' }], theme.newColors.neutral400]
        }
        return [rows, color]
    }, [rows, color])
    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: '',
                width: 60,
                headerName: '',
                cellClassName: 'textCenter',
                renderCell: function Input(params: GridRenderCellParams): React.ReactElement {
                    const rowIndex = tableRef.current.getSortedRowIds().indexOf(params.row.id)
                    return <SimpleStringCell {...params} value={{ value: rowIndex + 1, color: textColor }} />
                },
            },
            {
                field: 'username',
                headerName: t('submission.importList.socialMediaTableLabel'),
                flex: 2,
                hideSortIcons: true,
                sortable: false,
                renderCell: function Input(params: GridRenderCellParams): React.ReactElement {
                    return <SimpleStringCell {...params} value={{ value: params.value, color: textColor }} />
                },
            },
            {
                field: 'message',
                headerName: t('submission.importList.resultsTableLabel'),
                flex: 1,
                renderCell: function Input(params: GridRenderCellParams): React.ReactElement {
                    return <SimpleStringCell {...params} value={{ value: params.value, color: textColor }} />
                },
                valueGetter: ({ value }: GridValueGetterParams) => {
                    if (_.isEmpty(value)) {
                        return isPayout
                            ? t('submission.importList.processedTableStatus')
                            : isWaitlistAction
                              ? t('submission.importList.waitlisted', 'Waitlisted')
                              : t('submission.importList.approved', 'Approved')
                    }
                    if (value === ERROR_CODES.SUBMISSION_NOT_FOUND) {
                        return t('submission.importList.cantFindSubmissionTableStatus')
                    }
                    if (value === ERROR_CODES.SUBMISSION_PAID) {
                        return t('submission.importList.invalidAccountAlreadyPaidTableStatus')
                    }
                    if (value === ERROR_CODES.CAMPAIGN_NOT_FOUND) {
                        return t('errorMessage.validation.cantFindCampaign', 'We can’t find this campaign')
                    }
                    if (value === ERROR_CODES.SUBMISSION_PAYOUT_ON_PROCESS) {
                        return t('submission.importList.invalidAccountAlreadyProcessedTableStatus')
                    }
                    if (value === ERROR_CODES.DUPLICATE_SUBMISSION_RESULT_FOUND) {
                        return t('submssion.importList.invalidAccountMulitpleAccountTableStatus')
                    }
                    if (value === ERROR_CODES.USERNAME_FORMAT_INVALID) {
                        return t('errorMessage.validation.usernameCharacterRequirement')
                    } else if (value === ERROR_CODES.DUPLICATE_REGISTRATION_RESULT_FOUND) {
                        return t('submssion.importList.invalidAccountMulitpleAccountTableStatus')
                    } else if (value === ERROR_CODES.USER_NOT_REGISTER_INTEREST) {
                        return t('errorMessage.validation.noPendingRegistrations', 'No pending interest registration')
                    } else if (value === ERROR_CODES.REGISTRATION_RESULT_HAS_BEEN_REVIEWED) {
                        return t(
                            'modal.listApproval.error.accountAlreadyBeenReviewed',
                            'Account has already been reviewed',
                        )
                    } else if (value === ERROR_CODES.CAMPAIGN_EARLIEST_STEP_HAS_BEEN_PASSED) {
                        return t('errorMessage.validation.campaignDeadlineOver', 'Campaign deadline is over')
                    } else if (value === ERROR_CODES.REGISTRATION_RESULT_CANNOT_BE_APPROVED_DUE_TO_BLOCKED_CAMPAIGN) {
                        return t('errorMessage.businessLogic.partiposterInConflictingCampaignSubtitle', { count: 1 })
                    }
                    return value
                },
            },
        ],
        [],
    )

    return (
        <li {...props}>
            {title}
            <MassImportResultDataGrid
                apiRef={tableRef}
                rows={usernames}
                columns={columns}
                hideFooter
                autoHeight
                rowHeight={65}
                showCellRightBorder
                showColumnRightBorder
                disableSelectionOnClick
                disableColumnResize
                disableColumnMenu
            />
        </li>
    )
}

interface RowState {
    id: number | string
    username: string
    message?: string
}

interface MassImportResultProps {
    close: () => void
    actionType: ActionType
    successRows: RowState[]
    failedRows: RowState[]
    isPayout?: boolean
}

const MassImportResult: React.FC<MassImportResultProps> = ({
    close,
    actionType,
    isPayout = false,
    successRows,
    failedRows,
}) => {
    const [t] = useNewTranslation()
    const isWaitlistAction = ActionType.WAITLIST === actionType
    const processedCountBoxTitle = useMemo(() => {
        switch (actionType) {
            case ActionType.PAYOUT: {
                return t('submission.importList.processedAccountHeader')
            }
            case ActionType.WAITLIST: {
                return t('submission.importList.waitlistedAccounts', 'Waitlisted accounts')
            }
            default:
                return t('submission.importList.approvedAccounts', 'Approved accounts')
        }
    }, [actionType])
    const descriptionTextContent = useMemo(() => {
        if (failedRows.length > 0) {
            if (isPayout) {
                return t('submission.importList.checkInvalidAccountInstruction')
            }
            if (isWaitlistAction) {
                return t(
                    'modal.waitlist.result.error',
                    'Please take note of the following outcomes below. We suggest noting down the invalid accounts and adding them to the waitlist outside of the import list page.',
                )
            } else {
                return t(
                    'modal.listApproval.result.error',
                    'Please take note of the following outcomes below. We suggest noting down the invalid accounts and approving them outside of list approval.',
                )
            }
        }
        if (isWaitlistAction) {
            return t('modalWindow.waitlistSuccessfulSubtitle', 'All accounts were successfully waitlisted!')
        } else {
            return t('submission.importList.accountsApprovedStatusMessage')
        }
    }, [failedRows])

    return (
        <>
            <ScrollBox>
                <Flex my={theme.spacing.xs} mx={theme.spacing.l} justifyContent="space-around">
                    <CountBox
                        title={t('submission.importList.totalAccountHeader')}
                        count={failedRows.length + successRows.length}
                    />
                    <CountBox
                        title={processedCountBoxTitle}
                        count={successRows.length}
                        color={isWaitlistAction ? theme.colors.orange : theme.colors.governorBay}
                    />
                    <CountBox
                        title={t('submission.importList.invalidAccountHeader')}
                        count={failedRows.length}
                        color={theme.colors.monza}
                    />
                </Flex>
                <Box my={theme.spacing.xxs}>
                    <DescriptionText>{descriptionTextContent}</DescriptionText>
                    <Text mb={theme.spacing.xxs}>
                        <TableContainer>
                            <ol style={{ paddingInlineStart: '20px' }}>
                                <Table
                                    data-testid="invalid-table"
                                    rows={failedRows}
                                    title={t('submission.importList.invalidAccountHeader')}
                                    color={theme.colors.monza}
                                />
                                <Table
                                    data-testid="success-table"
                                    rows={successRows}
                                    isWaitlistAction={isWaitlistAction}
                                    isPayout={isPayout}
                                    title={
                                        isWaitlistAction
                                            ? t('submission.importList.waitlistedAccounts', 'Waitlisted accounts')
                                            : t('submission.importList.approvedAccountTableTitle')
                                    }
                                    color={isWaitlistAction ? theme.colors.orange : theme.colors.governorBay}
                                />
                            </ol>
                        </TableContainer>
                    </Text>
                </Box>
            </ScrollBox>
            <Flex justifyContent="right" my={theme.spacing.s}>
                <ButtonPrimary themeColor="astronaut" textProps={{ fontWeight: 'normal' }} onClick={close}>
                    {t('button.close')}
                </ButtonPrimary>
            </Flex>
        </>
    )
}

export default MassImportResult
